// THE navigation manifest — the single source of truth for what the app's
// sections and pages are, where they live, and which access FEATURE gates each.
//
// It drives: the Header (top-level items + dropdown menus + the mobile
// accordion), each section's pill strip, the landing redirect, the route
// guards (<RequireFeature/>), and the Admin → Access screen's feature labels.
// Before this file the same information lived in six unrelated arrays
// (Header NAV_ITEMS, TradingDesk PILLS, FundFacts/Uploads/Digit TABS, Accounts
// TABS) that had to be edited in lockstep.
//
// Feature keys are DOTTED and hierarchical, matching config/access.py on the
// backend: a grant of "trading-desk" covers "trading-desk.cash"; "admin.*"
// covers every admin page. The backend gates its blueprints by the same keys
// (see the guard table in flask-back-end/app.py), so hiding here and
// enforcing there can never disagree about what a feature is called.

export interface NavItem {
  key: string;
  label: string;
  to: string;
  feature: string;
  /** Short hint shown under the label in the dropdown. */
  hint?: string;
  /** Checkpoint keeps its own, older allow-list (toolBox.checkpointAccess);
   *  the Header shows it only when that check passes. */
  requiresCheckpoint?: boolean;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

export interface NavSection {
  key: string;
  label: string;
  to: string;
  feature: string;
  /** Empty = a plain link (no dropdown). */
  groups: NavGroup[];
}

const td = (key: string, label: string, hint?: string): NavItem => ({
  key, label, to: `/trading-desk/${key}`, feature: `trading-desk.${key}`, hint,
});
const ff = (key: string, label: string, hint?: string): NavItem => ({
  key, label, to: `/fund-facts/${key}`, feature: `fund-facts.${key}`, hint,
});
const ac = (key: string, label: string, hint?: string): NavItem => ({
  key, label, to: `/accounts/${key}`, feature: `accounts.${key}`, hint,
});

export const NAV: NavSection[] = [
  {
    key: "trading-desk",
    label: "Trading Desk",
    to: "/trading-desk",
    feature: "trading-desk",
    groups: [
      {
        title: "Workflow",
        items: [
          // Iron Dome first: it was the Trading Desk's default pill and is the
          // desk's morning page, so it is also the landing page for the desk.
          td("iron-dome", "Iron Dome", "Risk screens and daily checks"),
          td("command-center", "Command Center", "One fund, every panel"),
          td("cash", "Cash Monitor", "Balances, orders, blotters"),
          // Labels renamed 2026-09-18 (operator): the KEYS stay `trade` / `blotter`
          // because they are the feature keys stored in AccessGrants and the
          // paths in every bookmark. FX Hedging is a Trade Builder sub-tab now.
          td("trade", "Trade Builder", "Invest cash, rebalance, FX hedging, commit"),
          // Moved out of Cash Monitor 2026-10-29 (operator): a trading tool, so it
          // sits beside Trade Builder. Its Bloomberg fills split per account on
          // Trade Allocations -> Partner (d1g1t).
          td("partner-trades", "Partner Trades", "d1g1t partner orders → Bloomberg blocks, basket, fills back"),
          td("blotter", "Trade Allocations", "End-of-day blotters and custodian advice files"),
          td("trading-stats", "Trading Stats", "Trades, $ traded, turnover, FX, NBIN cross-check"),
          td("money-movements", "Money Movement", "Settlements and receivables"),
          td("perpetual-hedging", "Perpetual Hedging", "SPX put-overlay backtester"),
          // Margin Reports is an Iron Dome sub-tab (trading-desk.iron-dome.margin-reports).
        ],
      },
      {
        title: "Reconciliation",
        items: [
          td("master-recon", "Master Recon", "NAV vs custody vs book"),
          td("position-recon", "Position Recon", "Positions and ACB"),
          td("fundserv-holdings", "Fundserv Holdings", "MYSERV holdings recon"),
          td("private-statements", "Private Statements", "Upload a statement, get the entries"),
        ],
      },
      {
        title: "Custodians & data",
        items: [
          td("bloomberg", "Bloomberg", "Query and pending orders"),
          td("ibkr", "IBKR", "Flex statements and cash sync"),
          td("sggg", "SGGG", "Fund administrator console"),
          td("sggg-books", "SGGG Books", "Trial balance, GL, book of record, commissions"),
        ],
      },
    ],
  },
  {
    key: "fund-facts",
    label: "Fund Facts",
    to: "/fund-facts",
    feature: "fund-facts",
    groups: [
      {
        title: "Documents",
        items: [
          ff("generate", "Fund Facts", "Generate the PDFs"),
          ff("v2", "Fund Facts V2", "Config-driven documents"),
          ff("etf-metadata", "ETF Metadata", "Factsheet extraction"),
        ],
      },
      {
        title: "Performance",
        items: [
          ff("performance", "Performance", "All funds, screened"),
          ff("performance-charts", "Performance Charts", "Multi-fund deep dive"),
          ff("models", "Models", "Blend funds into models"),
          ff("risk-ratings", "Risk Ratings", "Fund Facts risk rating, day by day"),
          ff("attribution", "Attribution", "Funds vs peers and index, flagged"),
          // Was an Iron Dome sub-tab; the backend routes stay on the iron_dome
          // blueprint, which lists this key as a consumer (app.py guard table).
          ff("shadow-nav", "Shadow NAV", "Estimated NAV vs the administrator's"),
        ],
      },
      {
        title: "Fund data",
        items: [
          // Fund Admin lives under Admin now (admin.fund-admin).
          ff("securities-lending", "Securities Lending", "On-loan income"),
          ff("distribution-yields", "Distribution Yields", "Bps per fund"),
          ff("tax-dashboard", "Tax Dashboard", "Year-end and CGRM"),
        ],
      },
    ],
  },
  {
    key: "accounts",
    label: "Accounts",
    to: "/accounts",
    feature: "accounts",
    groups: [
      {
        title: "Lookup",
        items: [
          ac("account", "Account", "One account, any day"),
          ac("book", "Book", "The whole book on a day"),
          ac("partners", "Partners", "Assets per partner"),
          ac("reports", "Reports", "Criteria to workbook"),
          ac("tax", "Tax", "Slips expected vs arrived; book cost vs SGGG; realized gains (T5008)"),
        ],
      },
      {
        title: "Tools",
        items: [
          ac("rebalance", "Rebalance Calculator", "Rebalance client accounts into a model"),
        ],
      },
      {
        title: "Oversight",
        items: [
          // Was an Iron Dome sub-tab. It sits here so the Compliance group can
          // hold it without any Trading Desk pill; it is LOCKED to Compliance +
          // the desk by grant — ops and finance get the Lookup/Tools pages one
          // by one rather than the `accounts` parent, so it never reaches them.
          ac("compliance", "Compliance", "Issuer ownership thresholds and sign-off"),
          // Granted on its own key (migration 69) to ops, finance and executive,
          // who hold no other Accounts page; the desk and Compliance inherit it.
          ac("error-accounts", "Error Accounts", "Positions + cash parked in NBIN error accounts"),
        ],
      },
    ],
  },
  {
    // Its own section rather than an Accounts page: Compliance and the desk
    // hold the `accounts` PARENT key, and a dotted child inherits its parent's
    // grant, so `accounts.revenue` would have shown firm revenue to both.
    key: "finance",
    label: "Finance",
    to: "/finance",
    feature: "finance",
    groups: [
      {
        title: "Revenue",
        items: [
          { key: "revenue", label: "Revenue Forecast", to: "/finance/revenue", feature: "finance.revenue",
            hint: "Fee revenue per partner from their QWP funds" },
        ],
      },
      {
        // Moved in from Accounts › Lookup (2026-09-23). It was keyed
        // `accounts.fees`, and NOTHING was ever granted that key -- the screen
        // was reachable only because four groups hold the `accounts` PARENT,
        // so Trading Ops and Compliance could read client fee data while the
        // team that owns it had it by the same accident. A `finance.*` key is
        // one nobody holds by inheritance, so the grant has to be deliberate.
        title: "Fees",
        items: [
          { key: "fees", label: "Fee Calc", to: "/finance/fees", feature: "finance.fees",
            hint: "Fees recalculated, with the bad data that produced them" },
          // Opens the full-screen Fees app (/fees): this path only redirects there.
          // A child of `finance.fees`, so it inherits that grant (the backend
          // gates on `finance.fees` either way).
          { key: "fee-app", label: "Fees app (new)", to: "/finance/fee-app", feature: "finance.fees.app",
            hint: "Calculate, review, match NBIN, close and report — one workspace" },
        ],
      },
    ],
  },
  {
    key: "power-bi",
    label: "Power BI",
    to: "/power-bi",
    feature: "power-bi",
    groups: [],
  },
  {
    // The partner-facing Command Center: one fund at a time, curated fields
    // only, served by its own blueprint (resources/fund_view). Its key is a
    // top-level one nobody inherits, so the `viewer` group can hold it and
    // nothing else. Placed after the internal sections so landingPath() still
    // sends every internal group to its usual first page.
    key: "fund-view",
    label: "Fund View",
    to: "/fund-view",
    feature: "fund-view",
    groups: [],
  },
  {
    key: "admin",
    label: "Admin",
    to: "/admin",
    feature: "admin",
    groups: [
      {
        title: "Data & jobs",
        items: [
          { key: "uploads-trades", label: "Trade Uploads", to: "/admin/uploads/trades", feature: "admin.uploads", hint: "Trade files and parsers" },
          { key: "uploads-excel", label: "Excel Upload", to: "/admin/uploads/excel", feature: "admin.uploads", hint: "Spreadsheet ingest" },
          // Moved in from Fund Facts › Fund data (2026-09-18): it edits fund
          // master records, which is administration rather than fund facts.
          { key: "fund-admin", label: "Fund Admin", to: "/admin/fund-admin", feature: "admin.fund-admin", hint: "Fund master records" },
          { key: "tasks", label: "Tasks", to: "/admin/tasks", feature: "admin.tasks", hint: "Scheduled job log" },
          { key: "automations", label: "Automations", to: "/admin/automations", feature: "admin.automations", hint: "Procs and pipeline triggers" },
          { key: "sftp-logs", label: "SFTP Logs", to: "/admin/sftp-logs", feature: "admin.sftp-logs", hint: "File transfer history" },
        ],
      },
      {
        title: "Access",
        items: [
          { key: "access", label: "Users & Groups", to: "/admin/access", feature: "admin.access", hint: "Who can use what" },
          // Database sits with Users & Groups because both are super-admin-only
          // by grant: the desk holds every other admin.* key but not these two.
          { key: "database", label: "Database", to: "/admin/database", feature: "admin.database", hint: "Snapshots, clones, restores" },
          { key: "checkpoint", label: "Checkpoint", to: "/checkpoint", feature: "admin.checkpoint", hint: "Procedures and file transfers", requiresCheckpoint: true },
        ],
      },
    ],
  },
];

/** Every page, in nav order. */
export const allNavItems = (): NavItem[] => NAV.flatMap((s) => s.groups.flatMap((g) => g.items));

/** The section whose path prefix matches `pathname` (e.g. "/trading-desk/cash/fx"). */
export const sectionForPath = (pathname: string): NavSection | undefined =>
  NAV.find((s) => pathname === s.to || pathname.startsWith(s.to + "/"));

/** The item whose path prefix matches `pathname`, if any. */
export const itemForPath = (pathname: string): NavItem | undefined => {
  const items = allNavItems().filter((i) => pathname === i.to || pathname.startsWith(i.to + "/"));
  // Longest match wins ("/admin/uploads/trades" over "/admin").
  return items.sort((a, b) => b.to.length - a.to.length)[0];
};

/**
 * Where a signed-in person should land: the first page they can open, in nav
 * order. `can` is the access hook's predicate. Falls back to Trading Desk so a
 * brand-new user with no grants still sees the app's front door (the backend
 * still gates every request).
 */
export const landingPath = (can: (feature: string) => boolean): string => {
  for (const s of NAV) {
    if (s.groups.length === 0) {
      if (can(s.feature)) return s.to;
      continue;
    }
    for (const g of s.groups) {
      for (const i of g.items) {
        if (!i.requiresCheckpoint && can(i.feature)) return i.to;
      }
    }
  }
  return "/trading-desk";
};

/** The keys the Admin → Access screen offers when typing a feature grant. */
export const featureCatalog = (): { key: string; label: string }[] => {
  const out: { key: string; label: string }[] = [{ key: "*", label: "Everything" }];
  for (const s of NAV) {
    out.push({ key: s.feature, label: `${s.label} (all)` });
    for (const g of s.groups) {
      for (const i of g.items) {
        if (!out.some((o) => o.key === i.feature)) out.push({ key: i.feature, label: `${s.label} › ${i.label}` });
      }
    }
  }
  // Inner pages the backend also gates, under their pill:
  out.push({ key: "trading-desk.iron-dome.margin-reports", label: "Trading Desk › Iron Dome › Margin Reports" });
  out.push({ key: "trading-desk.iron-dome.avg-price", label: "Trading Desk › Iron Dome › Avg Price" });
  // A SIBLING key, not a child of accounts.rebalance: a dotted child inherits its
  // parent's grant, so "accounts.rebalance" would cover it and Partner PMs
  // would see the model/restricted-asset editors.
  out.push({ key: "accounts.rebalance-config", label: "Accounts › Rebalance Calculator › Models & Restricted Assets (edit)" });
  // TOP-LEVEL on purpose: under `fund-facts` every group holding that section
  // (compliance, finance, the desk, ops) would inherit it. Setting a manager
  // risk rating changes what the fact sheet publishes.
  out.push({ key: "risk-rating-override", label: "Fund Facts › Risk Ratings › Set the manager risk rating" });
  // TOP-LEVEL on purpose: under `finance` the finance and executive groups
  // (both hold the `finance` parent) would inherit it. The firm's economics by
  // partner -- granted per PERSON (migration 93), hidden from everyone else.
  out.push({ key: "exec.partner-contribution", label: "Finance › Revenue Forecast › Partner contribution (hidden)" });
  return out;
};
